<template>
    <Modal :show="showModal" @close="hide">
        <div class="w-full space-y-8 overflow-y-auto p-4 md:p-6 lg:p-10">
            <h2 class="text-center text-2xl font-medium">
                <span v-if="offer.application_type == 'internal'">{{ $t('Apply on the offer :title', { title: offer.title }) }}</span>
                <span v-if="offer.application_type == 'external'">
                    {{ $t('Start the application process for :title', { title: offer.title }) }}
                </span>
            </h2>

            <div class="mt-12 flex justify-center">
                <img :src="offer.company.logo" alt="" class="h-24 w-64 object-contain" />
            </div>

            <!-- Blocks the application feature if it is not in an accepted country -->
            <div class="mt-24" v-if="!canApply">
                <div class="flex justify-center">
                    <div class="mx-auto flex h-24 w-24 flex-shrink-0 items-center justify-center rounded-full bg-red-100">
                        <IconAlertTriangle size="48" class="text-red-600" />
                    </div>
                </div>

                <div class="flex justify-center">
                    <div class="mt-3 text-center">
                        <h3 class="text-base font-semibold leading-6 text-gray-900" id="modal-title">
                            {{ $t('Job offer unavailable') }}
                        </h3>

                        <div class="mt-2">
                            <p class="text-sm text-gray-500">
                                {{
                                    $t(
                                        'Sorry, but this job offer is not available in your country. We understand that this can be disappointing, but unfortunately we have to respect geographical restrictions imposed by the employer or local regulations.'
                                    )
                                }}
                            </p>
                            <p class="mt-5 text-sm text-gray-500">
                                {{ $t('Thank you for your understanding and your interest in our website.') }}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Application form -->
            <ApplicationForm v-if="canApply" :offer="offer" @success="onSuccess" />
        </div>
    </Modal>
</template>

<script setup>
    import Modal from '@/Shared/Modal.vue';
    import { Inertia } from '@inertiajs/inertia';
    import { IconAlertTriangle } from '@tabler/icons-vue';
    import { trans } from 'laravel-vue-i18n';
    import Swal from 'sweetalert2';
    import { onMounted, ref } from 'vue';
    import ApplicationForm from './ApplicationForm.vue';

    const emit = defineEmits(['success']);

    const props = defineProps({
        offer: Object,
    });

    const showModal = ref(false);
    const canApply = ref(true);

    onMounted(() => {
        axios.get('https://api.aeroweb.ca/v1/camaq/user-country').then(response => {
            // Unless the offer is set to accept international applications, we can only apply from Canada or the US
            if (
                !props.offer.accept_international_applications &&
                response.data.country != 'Canada' &&
                response.data.country != 'United States'
            ) {
                canApply.value = false;
            }
        });
    });

    function show() {
        showModal.value = true;
        window.location.hash = 'application';
    }

    function hide() {
        showModal.value = false;
        window.location.hash = '';
    }

    function onSuccess() {
        hide();
        emit('success');

        setTimeout(() => {
            Swal.fire({
                title: 'Succès',
                text: trans('You have successfully applied for the position :title!', { title: props.offer.title }),
                icon: 'success',
                timer: 3000,
                timerProgressBar: true,
                showConfirmButton: false,
            });

            if (props.offer.redirection_url?.length) {
                setTimeout(() => {
                    window.location.href = props.offer.redirection_url;
                }, 2000);
            } else {
                Inertia.reload();
            }
        }, 500);
    }

    defineExpose({ show });
</script>
